import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import axiosInstance from '../../api/commonAPI';
import ProductImage from '../../components/ProductImage';
import useCartQuantities from '../../hooks/useCartQuantities';
import './OrderPage.css';

const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;

interface Category {
    id: number;
    name: string;
}

interface ProductWithCategory {
    id: number;
    name: string;
    price: number;
    description: string;
    imageUrl: string | null;
    categoryId: number;
    categoryName: string;
    soldOut: boolean;
}

interface TableItem {
    tableItemId: number;
    quantity: number;
    product: {
        productId: number;
        name: string;
        price: number;
        imageUrl: string | null;
    };
}

interface Order {
    orderId: number;
    storeId?: number;
    tableId?: number;
    orderNumber: number;
    status: 'NEW' | 'PROCESSING' | 'SERVED' | 'COMPLETED' | 'CANCELED';
    orderDateTime: string;
    totalPrice: number;
    settlementCode?: string;
    orderItems: {
        orderItemId?: number;
        productId: number;
        productName: string;
        quantity: number;
        price: number;
    }[];
}

interface OrderPresenceResponse {
    storeId: number;
    tableId: number;
    count: number;
}

type OrderPageParams = {
    storeId: string;
    tableId: string;
};

const statusText: Record<Order['status'], string> = {
    NEW: '주문 접수',
    PROCESSING: '조리 중',
    SERVED: '제공완료',
    COMPLETED: '완료',
    CANCELED: '취소',
};

const OrderCheckTab: React.FC<{
    orders: Order[];
    isLoading: boolean;
    error: string | null;
}> = ({ orders, isLoading, error }) => {
    // Serving changes only the status label. Only settlement clears the guest history.
    const filteredOrders = orders.filter(order => order.status !== 'COMPLETED');
    const activeOrderCount = filteredOrders.length;
    const activeOrderTotalPrice = filteredOrders.reduce((sum, order) => sum + order.totalPrice, 0);

    return (
        <section className="order-check-panel">
            <div className="order-check-header">
                <h2>주문 확인</h2>
                <div className="order-check-summary">
                    <span>전체 {activeOrderCount}건</span>
                    <strong>{activeOrderTotalPrice.toLocaleString()}원</strong>
                </div>
            </div>
            {isLoading ? (
                <p className="muted-text">주문 내역을 불러오는 중...</p>
            ) : error ? (
                <p className="error-message">{error}</p>
            ) : filteredOrders.length === 0 ? (
                <p className="muted-text">진행 중인 주문이 없습니다.</p>
            ) : (
                <>
                    {filteredOrders.slice().reverse().map(order => (
                        <article key={order.orderId} className="order-history-card">
                            <div className="order-history-header">
                                <strong>주문 #{order.orderNumber}</strong>
                                <span className="order-status">{statusText[order.status]}</span>
                            </div>
                            <p className="order-time">{new Date(order.orderDateTime).toLocaleString('ko-KR')}</p>
                            <ul className="order-item-list">
                                {order.orderItems.map(item => (
                                    <li key={item.productId}>
                                        <span>{item.productName}</span>
                                        <span>{item.quantity}개</span>
                                        <strong>{(item.price * item.quantity).toLocaleString()}원</strong>
                                    </li>
                                ))}
                            </ul>
                            <div className="order-history-total">
                                합계 {order.totalPrice.toLocaleString()}원
                            </div>
                        </article>
                    ))}
                </>
            )}
        </section>
    );
};

const OrderPage = () => {
    const { storeId, tableId } = useParams<OrderPageParams>();
    const [tableName, setTableName] = useState('');
    const [tableError, setTableError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        setTableName('');
        setTableError(null);
        if (!storeId || !tableId) return;
        axiosInstance.get<{ tableName: string }>(`/api/public/stores/${storeId}/tables/${tableId}`)
            .then(response => { if (active) setTableName(response.data.tableName); })
            .catch(() => { if (active) setTableError('테이블 정보를 불러오지 못했습니다. QR 코드를 다시 확인해 주세요.'); });
        return () => { active = false; };
    }, [storeId, tableId]);

    const [categories, setCategories] = useState<Category[]>([]);
    const [products, setProducts] = useState<ProductWithCategory[]>([]);
    const { items: tableItems, receiveItems: setTableItems, changeQuantity,
        isSaving: isSavingCart, syncError: cartSyncError, hasPendingChanges } = useCartQuantities(`/api/public/stores/${storeId}/tables/${tableId}/items`);
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [orders, setOrders] = useState<Order[]>([]);
    const liveOrderUpdates = useRef(new Map<number, Order>());
    const orderRequestId = useRef(0);
    useEffect(() => {
        liveOrderUpdates.current.clear();
        setOrders([]);
        return () => { orderRequestId.current += 1; };
    }, [storeId, tableId]);
    const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(true);
    const [isLoadingOrders, setIsLoadingOrders] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [orderError, setOrderError] = useState<string | null>(null);
    const [quantities, setQuantities] = useState<{ [productId: number]: number }>({});
    const [activeTab, setActiveTab] = useState<'menu' | 'cart' | 'orderCheck'>('menu');
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
    const [activeOrderUserCount, setActiveOrderUserCount] = useState<number>(1);

    const totalCartQuantity = useMemo(
        () => tableItems.reduce((acc, item) => acc + item.quantity, 0),
        [tableItems]
    );

    const fetchMenuData = useCallback(async () => {
        if (!storeId) return;
        setIsLoadingProducts(true);
        try {
            const [categoryResponse, productResponse] = await Promise.all([
                axiosInstance.get<Category[]>(`/api/public/stores/${storeId}/categories`),
                axiosInstance.get<ProductWithCategory[]>(`/api/public/stores/${storeId}/products`),
            ]);

            setCategories(categoryResponse.data);
            setProducts(productResponse.data);
            setError(null);

            const initialQuantities: { [key: number]: number } = {};
            productResponse.data.forEach(product => {
                initialQuantities[product.id] = 1;
            });
            setQuantities(initialQuantities);
        } catch {
            setError('메뉴를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
            setIsLoadingProducts(false);
        }
    }, [storeId]);

    const fetchTableItems = useCallback(async () => {
        if (!storeId || !tableId) return;
        try {
            const response = await axiosInstance.get<TableItem[]>(`/api/public/stores/${storeId}/tables/${tableId}/items`);
            setTableItems(response.data);
        } catch (err) {
            console.error('장바구니 조회 실패:', err);
        }
    }, [storeId, tableId, setTableItems]);

    const fetchOrders = useCallback(async () => {
        if (!storeId || !tableId) return;
        const requestId = ++orderRequestId.current;
        const updatesAtStart = new Map(liveOrderUpdates.current);
        setIsLoadingOrders(true);
        try {
            const response = await axiosInstance.get<Order[]>(`/api/public/stores/${storeId}/tables/${tableId}/orders`);
            if (requestId !== orderRequestId.current) return;
            const merged = new Map(response.data.map(order => [order.orderId, order]));
            // A slower HTTP snapshot must not erase an order or serving update received live.
            liveOrderUpdates.current.forEach((order, id) => {
                if (updatesAtStart.get(id) !== order) merged.set(id, order);
            });
            setOrders(Array.from(merged.values()).sort((a, b) => b.orderId - a.orderId));
            setOrderError(null);
        } catch {
            if (requestId === orderRequestId.current) setOrderError('주문 내역을 불러오지 못했습니다.');
        } finally {
            if (requestId === orderRequestId.current) setIsLoadingOrders(false);
        }
    }, [storeId, tableId]);

    useEffect(() => {
        if (!storeId || !tableId) {
            setError('잘못된 접근입니다. QR 코드를 다시 스캔해 주세요.');
            setIsLoadingProducts(false);
            return;
        }

        const fetchData = async () => {
            await fetchMenuData();
            await fetchTableItems();
            await fetchOrders();
        };
        fetchData();
    }, [storeId, tableId, fetchMenuData, fetchTableItems, fetchOrders]);

    useEffect(() => {
        if (categories.length === 0) {
            setSelectedCategoryId(null);
            return;
        }

        const selectedCategoryExists = categories.some(category => category.id === selectedCategoryId);
        if (selectedCategoryId === null || !selectedCategoryExists) {
            setSelectedCategoryId(categories[0].id);
        }
    }, [categories, selectedCategoryId]);

    useEffect(() => {
        if (!storeId || !tableId) return;

        const presenceClient = new Client({
            webSocketFactory: () => new SockJS(`${baseUrl}/ws`),
            reconnectDelay: 5000,
            onConnect: () => {
                presenceClient.subscribe(`/topic/order-presence/${storeId}/${tableId}`, (message) => {
                    if (!message.body) return;

                    const presence: OrderPresenceResponse = JSON.parse(message.body);
                    setActiveOrderUserCount(Math.max(1, presence.count));
                });

                presenceClient.subscribe(`/topic/table-items/${storeId}/${tableId}`, (message) => {
                    if (!message.body) return;

                    const nextItems: TableItem[] = JSON.parse(message.body);
                    setTableItems(nextItems);
                });

                presenceClient.subscribe('/topic/orders', (message) => {
                    if (!message.body) return;

                    const nextOrder: Order = JSON.parse(message.body);
                    if (nextOrder.tableId !== Number(tableId)) return;
                    if (nextOrder.storeId != null && nextOrder.storeId !== Number(storeId)) return;
                    liveOrderUpdates.current.set(nextOrder.orderId, nextOrder);

                    setOrders(prevOrders => {
                        const index = prevOrders.findIndex(order => order.orderId === nextOrder.orderId);
                        if (index === -1) {
                            return [nextOrder, ...prevOrders];
                        }

                        const updatedOrders = [...prevOrders];
                        updatedOrders[index] = nextOrder;
                        return updatedOrders;
                    });
                    setOrderError(null);
                    setIsLoadingOrders(false);
                });

                presenceClient.publish({
                    destination: '/app/order-presence/join',
                    body: JSON.stringify({
                        storeId: Number(storeId),
                        tableId: Number(tableId),
                    }),
                });
            },
        });

        presenceClient.activate();

        return () => {
            if (presenceClient.connected) {
                presenceClient.publish({
                    destination: '/app/order-presence/leave',
                    body: JSON.stringify({
                        storeId: Number(storeId),
                        tableId: Number(tableId),
                    }),
                });
            }
            presenceClient.deactivate();
        };
    }, [storeId, tableId, setTableItems]);

    const increaseQuantity = (productId: number) => {
        setQuantities(prev => ({
            ...prev,
            [productId]: (prev[productId] ?? 1) + 1
        }));
    };

    const decreaseQuantity = (productId: number) => {
        setQuantities(prev => {
            const current = prev[productId] ?? 1;
            if (current <= 1) return prev;
            return {
                ...prev,
                [productId]: current - 1
            };
        });
    };

    const handleAddToCart = async (product: ProductWithCategory, quantity: number) => {
        if (!storeId || !tableId || hasPendingChanges() || isAddingToCart) return;
        if (product.soldOut) {
            alert('품절된 상품입니다.');
            return;
        }

        setIsAddingToCart(true);
        try {
            await axiosInstance.post(`/api/public/stores/${storeId}/tables/${tableId}/items`, {
                productId: product.id,
                quantity,
            });
            await fetchTableItems();
        } catch {
            alert('장바구니에 상품을 담지 못했습니다.');
        } finally {
            setIsAddingToCart(false);
        }
    };

    const handleOrderSubmit = async () => {
        if (!storeId || !tableId || hasPendingChanges() || isAddingToCart) return;
        if (tableItems.length === 0) {
            alert('주문할 상품이 없습니다. 메뉴에서 상품을 담아 주세요.');
            return;
        }
        if (window.confirm('주문을 완료할까요?')) {
            try {
                await axiosInstance.post('/api/public/orders', {
                    storeId: Number(storeId),
                    tableId: Number(tableId),
                    items: tableItems.map(item => ({
                        productId: item.product.productId,
                        quantity: item.quantity,
                    })),
                });
                alert('주문이 완료되었습니다. 잠시만 기다려 주세요.');
                setTableItems([]);
                await fetchTableItems();
                fetchOrders();
                setActiveTab('orderCheck');
            } catch {
                alert('주문 처리 중 오류가 발생했습니다. 직원에게 문의해 주세요.');
            }
        }
    };

    const totalPrice = useMemo(() => {
        return tableItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
    }, [tableItems]);

    const visibleProducts = useMemo(() => {
        if (selectedCategoryId === null) return products;
        return products.filter(product => product.categoryId === selectedCategoryId);
    }, [products, selectedCategoryId]);

    const selectedCategoryName = useMemo(() => {
        return categories.find(category => category.id === selectedCategoryId)?.name || '메뉴';
    }, [categories, selectedCategoryId]);

    if (error || tableError) {
        return <div className="order-page-container error-screen" role="alert"><h2>{tableError || error}</h2></div>;
    }

    if (isLoadingProducts) {
        return <div className="order-page-container loading-screen"><h2>메뉴를 불러오는 중...</h2></div>;
    }

    return (
        <div className="order-page-container">
            <header className="order-header">
                <div className="order-header-top">
                    <div>
                        <h1>{tableName ? `${tableName} 번 테이블 주문` : '테이블 주문'}</h1>
                        <p></p>
                    </div>
                </div>
                <div className="order-presence-badge">
                    {activeOrderUserCount}명이 함께 주문 중
                </div>
            </header>

            <main className="order-main-content">
                {activeTab === 'menu' && (
                    <section className="menu-layout">
                        <aside className="category-rail" aria-label="카테고리">
                            {categories.length > 0 ? (
                                categories.map(category => (
                                    <button
                                        key={category.id}
                                        className={selectedCategoryId === category.id ? 'category-button active' : 'category-button'}
                                        onClick={() => setSelectedCategoryId(category.id)}
                                        type="button"
                                    >
                                        {category.name}
                                    </button>
                                ))
                            ) : (
                                <p className="empty-category-message">카테고리 없음</p>
                            )}
                        </aside>

                        <section className="product-list-section">
                            <div className="product-section-header">
                                <h2>{selectedCategoryName}</h2>
                                <span>{visibleProducts.length}개</span>
                            </div>

                            {visibleProducts.length > 0 ? (
                                <div className="product-grid">
                                    {visibleProducts.map(product => (
                                        <article
                                            key={product.id}
                                            className={product.soldOut ? 'product-card sold-out' : 'product-card'}
                                        >
                                            <div className="product-image-wrap">
                                                <ProductImage
                                                    src={product.imageUrl || undefined}
                                                    alt={product.name}
                                                    className="product-image"
                                                />
                                                {product.soldOut && <span className="sold-out-badge">품절</span>}
                                            </div>
                                            <div className="product-info">
                                                <h3 className="product-name">{product.name}</h3>
                                                {product.description && (
                                                    <p className="product-description">{product.description}</p>
                                                )}
                                                <p className="product-price">{product.price.toLocaleString()}원</p>
                                            </div>
                                            <div className="quantity-controls">
                                                <button
                                                    onClick={() => decreaseQuantity(product.id)}
                                                    className="qty-btn"
                                                    type="button"
                                                    disabled={product.soldOut}
                                                >
                                                    -
                                                </button>
                                                <span className="quantity-value">{quantities[product.id] ?? 1}</span>
                                                <button
                                                    onClick={() => increaseQuantity(product.id)}
                                                    className="qty-btn"
                                                    type="button"
                                                    disabled={product.soldOut}
                                                >
                                                    +
                                                </button>
                                            </div>
                                            <button
                                                onClick={() => handleAddToCart(product, quantities[product.id] ?? 1)}
                                                className="add-to-cart-btn"
                                                type="button"
                                                disabled={product.soldOut || isSavingCart || !!cartSyncError || isAddingToCart}
                                            >
                                                {product.soldOut ? '품절' : '담기'}
                                            </button>
                                        </article>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-product-panel">
                                    <p>이 카테고리에 등록된 상품이 없습니다.</p>
                                </div>
                            )}
                        </section>
                    </section>
                )}

                {activeTab === 'cart' && (
                    <aside className="cart-section">
                        <h2>장바구니</h2>
                        {cartSyncError && <div role="alert">
                            <p>{cartSyncError}</p>
                            <button type="button" onClick={fetchTableItems}>다시 불러오기</button>
                        </div>}
                        <div className="cart-items">
                            {tableItems.length > 0 ? (
                                tableItems.map(item => (
                                    <div key={item.tableItemId} className="cart-item">
                                        <div className="cart-item-main">
                                            <span className="item-name">{item.product.name}</span>
                                            <span className="item-price">{(item.product.price * item.quantity).toLocaleString()}원</span>
                                        </div>
                                        <div className="cart-item-controls">
                                            <button
                                                onClick={() => changeQuantity(item, -1)}
                                                disabled={!!cartSyncError || isAddingToCart}
                                                className="cart-qty-btn"
                                                type="button"
                                                aria-label={`${item.product.name} 수량 줄이기`}
                                            >
                                                -
                                            </button>
                                            <span className="item-quantity">{item.quantity}개</span>
                                            <button
                                                onClick={() => changeQuantity(item, 1)}
                                                disabled={!!cartSyncError || isAddingToCart}
                                                className="cart-qty-btn"
                                                type="button"
                                                aria-label={`${item.product.name} 수량 늘리기`}
                                            >
                                                +
                                            </button>
                                            <button
                                                onClick={() => changeQuantity(item, 'remove')}
                                                disabled={!!cartSyncError || isAddingToCart}
                                                className="cart-delete-btn"
                                                type="button"
                                            >
                                                삭제
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="empty-cart-message">장바구니가 비어 있습니다.</p>
                            )}
                        </div>
                        <div className="cart-summary">
                            <h3>총 결제 금액</h3>
                            <p className="total-price">{totalPrice.toLocaleString()}원</p>
                        </div>
                        <button onClick={handleOrderSubmit} className="order-submit-btn" disabled={tableItems.length === 0 || isSavingCart || !!cartSyncError || isAddingToCart}>
                            {isSavingCart || isAddingToCart ? '장바구니 저장 중...' : '주문하기'}
                        </button>
                    </aside>
                )}

                {activeTab === 'orderCheck' && (
                    <OrderCheckTab
                        orders={orders}
                        isLoading={isLoadingOrders}
                        error={orderError}
                    />
                )}
            </main>

            <nav className="order-tab-nav">
                <button
                    className={activeTab === 'menu' ? 'tab-button active' : 'tab-button'}
                    onClick={() => setActiveTab('menu')}
                    type="button"
                >
                    메뉴
                </button>

                <button
                    className={`tab-button${activeTab === 'cart' ? ' active' : ''}${totalCartQuantity > 0 ? ' cart-has-items' : ''}`}
                    onClick={() => setActiveTab('cart')}
                    type="button"
                >
                    장바구니
                    {totalCartQuantity > 0 && <span className="cart-badge">{totalCartQuantity}</span>}
                </button>

                <button
                    className={activeTab === 'orderCheck' ? 'tab-button active' : 'tab-button'}
                    onClick={() => setActiveTab('orderCheck')}
                    type="button"
                >
                    주문 확인
                </button>
            </nav>
        </div>
    );
};

export default OrderPage;
